import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Resolve backend-stored file paths (e.g. "uploads/xyz.jpg") to full URLs.
// Passes auth token via query for <img> tags (which can't set Authorization headers).
export function resolveFileUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://") || pathOrUrl.startsWith("data:") || pathOrUrl.startsWith("blob:")) return pathOrUrl;
  const token = localStorage.getItem("sp_token") || "";
  const clean = pathOrUrl.replace(/^\/+/, "");
  const withApi = clean.startsWith("api/") ? clean : (clean.startsWith("files/") ? `api/${clean}` : `api/files/${clean}`);
  return `${BACKEND_URL}/${withApi}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
}

// Paths in /admin that reuse tenant endpoints (Clients CRM, Pipeline, Publish,
// Email marketing, Tags). When the user is on one of these routes, we inject
// the ``X-Workspace: agency-hq`` header so the backend silently scopes the
// agency_admin to their internal HQ tenant (data isolated from real salons).
const HQ_PATHS = [
  "/admin/clients",
  "/admin/pipeline",
  "/admin/publish",
  "/admin/email",
  "/admin/tags",
  "/admin/settings",
  "/admin/inbox",
  "/admin/automations",
];

function isHqPath(p) {
  if (!p) return false;
  return HQ_PATHS.some((prefix) => p === prefix || p.startsWith(prefix + "/"));
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("sp_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Never inject the HQ workspace header on /auth/* — those endpoints must
  // return the user's real role (agency_admin) so the frontend routing stays
  // correct while browsing the shared /admin CRM pages.
  // Also skip /admin/* — those endpoints require the real agency_admin role
  // (would return 403 if the header downgraded them to salon_owner of HQ).
  const url = (config.url || "");
  const isAuth = url.startsWith("/auth/") || url === "/auth/me";
  const isAdminApi = url.startsWith("/admin/");
  // Skip /agency/* too: those endpoints require the real agency_admin /
  // agency_receptionist role (the HQ header would downgrade to salon_owner → 403).
  const isAgencyApi = url.startsWith("/agency/");
  if (!isAuth && !isAdminApi && !isAgencyApi && typeof window !== "undefined" && isHqPath(window.location.pathname)) {
    config.headers["X-Workspace"] = "agency-hq";
  }
  return config;
});

// Dedicated axios instance for the Agency Workspace. It injects
// ``X-Act-As-Tenant`` (server-side authorized) on every request and NEVER the
// HQ ``X-Workspace`` header — the two contexts must stay separate. Isolated
// from the global ``api`` interceptor so admin aggregate calls are unaffected.
export function createAgencyClient(getTenantId) {
  const inst = axios.create({ baseURL: API, withCredentials: true });
  inst.interceptors.request.use((config) => {
    const token = localStorage.getItem("sp_token");
    if (token) config.headers.Authorization = `Bearer ${token}`;
    const tid = typeof getTenantId === "function" ? getTenantId() : getTenantId;
    if (tid) config.headers["X-Act-As-Tenant"] = tid;
    return config;
  });
  return inst;
}

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      // Do not force redirect from the auth-callback hash flow
      const hash = window.location.hash || "";
      if (hash.includes("session_id=")) return Promise.reject(err);
      // Un 401 da un endpoint Meta (token Meta scaduto/non valido) NON è una
      // sessione app scaduta: non sloggare l'utente.
      if ((err?.config?.url || "").includes("/meta/")) return Promise.reject(err);
      localStorage.removeItem("sp_token");
      localStorage.removeItem("sp_user");
      const path = window.location.pathname;
      if (!path.startsWith("/login") && !path.startsWith("/register") && !path.startsWith("/legal") && !path.startsWith("/privacy") && !path.startsWith("/terms") && !path.startsWith("/data-deletion") && !path.startsWith("/accept-invite") && path !== "/") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);
